import { KnownDeliveryDtoDeliveryType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { SvgIconTypeMap } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { Search } from '@mui/icons-material';
import { useProjectLabels } from '../../../../../hooks/use-entity-labels.hook';
import { Scan } from '../../../../../ui/components/pure/icons/Scan.icon';
import { SVGIconComponent } from '../../../../../ui/components/pure/icons/types';
import { useReceiveDeliveryFormData } from './receive-delivery-form-data.hook';

export interface ReceiveDeliveryForm {
  projectID: string;
  hubID: string;
  deliveryType: KnownDeliveryDtoDeliveryType;
  deliveringParty: string;
  receivingParty: string;
  deliveryContainerID: string;
  carrierReference?: string;
}

interface FormConfigItemBase {
  isVisible: boolean;
  isRequired: boolean;
  label: string;
  name: keyof ReceiveDeliveryForm;
  isWide?: boolean;
  EndAdornment?: SVGIconComponent | OverridableComponent<SvgIconTypeMap<{}, 'svg'>>;
  StartAdornment?: SVGIconComponent | OverridableComponent<SvgIconTypeMap<{}, 'svg'>>;
}

export interface ReceiveDeliverySelectOption {
  value: string;
  label: string;
  imageURL?: string;
  Icon?: SVGIconComponent;
  category?: string;
  unit?: string;
}

type FieldsWithSelectInput = Pick<ReceiveDeliveryForm, 'projectID' | 'deliveryType' | 'hubID' | 'deliveryContainerID'>;
type FieldsWithAutoComplete = Pick<ReceiveDeliveryForm, 'deliveringParty' | 'receivingParty'>;
type FieldsWithTextInput = Pick<ReceiveDeliveryForm, 'carrierReference'>;

export type ReceiveDeliveryFormConfig = {
  [key in keyof FieldsWithSelectInput]-?: FormConfigItemBase & {
    type: 'select';
    options: ReceiveDeliverySelectOption[];
  };
} & {
  [key in keyof FieldsWithAutoComplete]-?: FormConfigItemBase & {
    type: 'autocomplete';
    options: ReceiveDeliverySelectOption[];
  };
} & {
  [key in keyof FieldsWithTextInput]-?: FormConfigItemBase & {
    type: 'text';
  };
};

export interface ReceiveDeliveryFormConfigProps {
  projectID: string;
  selectedDeliveryType: KnownDeliveryDtoDeliveryType;
  selectedHubID: string;
  selectedProjectID: string;
}

export function useReceiveDeliveryFormConfig(props: ReceiveDeliveryFormConfigProps) {
  const projectLabel = useProjectLabels(props.projectID).singular;

  const deliveringPartyLabel = [
    KnownDeliveryDtoDeliveryType.InboundParcel,
    KnownDeliveryDtoDeliveryType.InboundMail
  ].includes(props.selectedDeliveryType)
    ? 'Delivered by'
    : [
        KnownDeliveryDtoDeliveryType.PeerToPeer,
        KnownDeliveryDtoDeliveryType.OutboundMail,
        KnownDeliveryDtoDeliveryType.OutboundDryCleaning
      ].includes(props.selectedDeliveryType)
    ? 'From resident'
    : 'From provider';

  const receivingPartyLabel = [
    KnownDeliveryDtoDeliveryType.InboundParcel,
    KnownDeliveryDtoDeliveryType.InboundMail,
    KnownDeliveryDtoDeliveryType.PeerToPeer,
    KnownDeliveryDtoDeliveryType.InboundDryCleaning
  ].includes(props.selectedDeliveryType)
    ? 'To resident'
    : 'To provider';

  const showCarrierReference = [
    KnownDeliveryDtoDeliveryType.InboundParcel,
    KnownDeliveryDtoDeliveryType.OutboundDryCleaning,
    KnownDeliveryDtoDeliveryType.InboundDryCleaning
  ].includes(props.selectedDeliveryType);

  const carrierReferenceLabel =
    props.selectedDeliveryType === KnownDeliveryDtoDeliveryType.InboundParcel
      ? 'Tracking code or description'
      : props.selectedDeliveryType === KnownDeliveryDtoDeliveryType.OutboundDryCleaning
      ? 'Order ID'
      : props.selectedDeliveryType === KnownDeliveryDtoDeliveryType.InboundDryCleaning
      ? 'Order ID'
      : '';

  const formData = useReceiveDeliveryFormData(props);

  const formConfig: ReceiveDeliveryFormConfig = {
    projectID: {
      type: 'select',
      isRequired: true,
      isVisible: true,
      label: projectLabel,
      name: 'projectID',
      options: formData.options.project
    },
    deliveryType: {
      type: 'select',
      isRequired: true,
      isVisible: true,
      label: 'Type',
      name: 'deliveryType',
      options: formData.options.deliveryType
    },
    carrierReference: {
      isRequired: false,
      isVisible: showCarrierReference,
      label: carrierReferenceLabel,
      name: 'carrierReference',
      type: 'text',
      EndAdornment: Scan
    },
    deliveringParty: {
      type: 'autocomplete',
      isRequired: true,
      isVisible: true,
      label: deliveringPartyLabel,
      name: 'deliveringParty',
      options: formData.options.deliveringParty,
      EndAdornment: Search
    },
    receivingParty: {
      type: 'autocomplete',
      isRequired: true,
      isVisible: true,
      label: receivingPartyLabel,
      name: 'receivingParty',
      options: formData.options.receivingParty,
      EndAdornment: Search
    },
    hubID: {
      type: 'select',
      isRequired: true,
      isVisible: true,
      label: 'Hub',
      name: 'hubID',
      options: formData.options.hub
    },
    deliveryContainerID: {
      type: 'select',
      isRequired: true,
      isVisible: true,
      label: 'Store in space',
      name: 'deliveryContainerID',
      options: formData.options.space
    }
  };

  return { formConfig, formData };
}
